import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Divider
} from '@mui/material';
import authService from '../../services/authService';

// Item 89 Phase B — login is now a small state machine:
//   credentials → (challenge | enrol) → [recovery-display] → dashboard
const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // `disabled={loading}` only lands after a re-render, so a double-click on
  // Sign In sent two logins and, for someone not yet enrolled, two two-factor
  // set-ups — each mints a new secret, and if the replies crossed, the QR on
  // screen would never match the one stored (19 Sep 2026 sign-up audit).
  const busyRef = useRef(false);
  const [stage, setStage] = useState('credentials'); // credentials | challenge | enrol | recovery
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 2FA state
  const [challengeToken, setChallengeToken] = useState(null);
  const [enrolToken, setEnrolToken] = useState(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [qr, setQr] = useState(null);          // { qrDataUrl, secret }
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  // If already signed in, skip straight to the app.
  useEffect(() => {
    if (authService.isAuthenticated()) navigate('/dashboard');
  }, [navigate]);

  // Item 89 — handoff from Register under forced 2FA: an enrol token arrives
  // via router state. Jump straight to the enrol screen and load its QR.
  useEffect(() => {
    const handoff = location.state?.enrolToken;
    if (!handoff) return;
    setEnrolToken(handoff);
    setStage('enrol');
    authService.start2faEnrol(handoff)
      .then((r) => setQr(r.data))
      .catch(() => setError('Enrolment session expired. Please sign in again.'));
    // clear the router state so a refresh doesn't re-trigger
    window.history.replaceState({}, document.title);
  }, [location.state]);

  const finalize = () => { window.location.href = '/dashboard'; };
  const msg = (err, fallback) => setError(err?.response?.data?.message || fallback);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // ---- Step 1: password -----------------------------------------------------
  const handleCredentials = async (e) => {
    e.preventDefault();
    setError(null);
    if (busyRef.current) return;   // double-click / double-submit: one request, not two
    busyRef.current = true;
    setLoading(true);
    try {
      const res = await authService.login(formData.email, formData.password);
      const data = res.data || {};
      if (data.token) return finalize();                 // policy-off path
      const tf = data.twoFactor;
      if (tf?.stage === 'challenge') {
        setChallengeToken(tf.challengeToken);
        setCode(''); setUseRecovery(false);
        setStage('challenge');
      } else if (tf?.stage === 'enrol') {
        setEnrolToken(tf.enrolToken);
        // fetch the QR immediately so the enrol screen is ready
        const enrolRes = await authService.start2faEnrol(tf.enrolToken);
        setQr(enrolRes.data);
        setCode('');
        setStage('enrol');
      } else {
        setError('Unexpected login response. Please try again.');
      }
    } catch (err) {
      msg(err, 'Login failed');
    } finally {
      busyRef.current = false;
      setLoading(false);
    }
  };

  // ---- Step 2a: challenge (existing 2FA) ------------------------------------
  const handleChallenge = async (e) => {
    e.preventDefault();
    setError(null);
    if (busyRef.current) return;   // double-click / double-submit: one request, not two
    busyRef.current = true;
    setLoading(true);
    try {
      const payload = useRecovery ? { recoveryCode: code } : { code };
      const res = await authService.verify2fa(challengeToken, payload);
      if (res.success) finalize();
    } catch (err) {
      msg(err, 'That code is not valid.');
    } finally {
      busyRef.current = false;
      setLoading(false);
    }
  };

  // ---- Step 2b: forced enrolment --------------------------------------------
  const handleEnrol = async (e) => {
    e.preventDefault();
    setError(null);
    if (busyRef.current) return;   // double-click / double-submit: one request, not two
    busyRef.current = true;
    setLoading(true);
    try {
      const res = await authService.complete2faEnrol(enrolToken, code);
      if (res.success) {
        setRecoveryCodes(res.data.recoveryCodes || []);
        setStage('recovery');
      }
    } catch (err) {
      msg(err, 'That code is not valid. Check your authenticator app.');
    } finally {
      busyRef.current = false;
      setLoading(false);
    }
  };

  const shell = (subtitle, children) => (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-page)' }}>
      <Card sx={{ maxWidth: 420, width: '100%', mx: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom textAlign="center">Kodahfy</Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>{subtitle}</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {children}
        </CardContent>
      </Card>
    </Box>
  );

  // ---- Render ---------------------------------------------------------------
  if (stage === 'challenge') {
    return shell('Two-factor authentication', (
      <form onSubmit={handleChallenge}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {useRecovery
            ? 'Enter one of your recovery codes.'
            : 'Enter the 6-digit code from your authenticator app.'}
        </Typography>
        <TextField
          fullWidth
          label={useRecovery ? 'Recovery code' : 'Authentication code'}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          margin="normal"
          autoFocus
          inputProps={{ inputMode: useRecovery ? 'text' : 'numeric', autoComplete: 'one-time-code' }}
        />
        <Button type="submit" fullWidth variant="contained" size="large" disabled={loading || !code} sx={{ mt: 2, mb: 2 }}>
          {loading ? <CircularProgress size={24} /> : 'Verify'}
        </Button>
        <Typography variant="body2" textAlign="center">
          <Link component="button" type="button" onClick={() => { setUseRecovery(!useRecovery); setCode(''); setError(null); }}>
            {useRecovery ? 'Use your authenticator app instead' : "Can't access your app? Use a recovery code"}
          </Link>
        </Typography>
      </form>
    ));
  }

  if (stage === 'enrol') {
    return shell('Set up two-factor authentication', (
      <form onSubmit={handleEnrol}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Two-factor authentication is required. Scan this QR code with an authenticator
          app (Google Authenticator, Authy, 1Password…), then enter the 6-digit code it shows.
        </Typography>
        {qr?.qrDataUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <img src={qr.qrDataUrl} alt="2FA QR code" style={{ width: 180, height: 180 }} />
          </Box>
        )}
        {qr?.secret && (
          <Typography variant="caption" color="text.secondary" component="div" textAlign="center" sx={{ mb: 2, wordBreak: 'break-all' }}>
            Can't scan? Enter this key manually: <strong>{qr.secret}</strong>
          </Typography>
        )}
        <TextField
          fullWidth
          label="6-digit code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          margin="normal"
          autoFocus
          inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
        />
        <Button type="submit" fullWidth variant="contained" size="large" disabled={loading || !code} sx={{ mt: 2, mb: 1 }}>
          {loading ? <CircularProgress size={24} /> : 'Verify & continue'}
        </Button>
      </form>
    ));
  }

  if (stage === 'recovery') {
    return shell('Save your recovery codes', (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Save these codes somewhere safe. Each works once and lets you sign in if you
          lose your phone. <strong>They won't be shown again.</strong>
        </Alert>
        <Box sx={{ p: 2, mb: 2, backgroundColor: 'var(--bg-subtle, #f5f5f5)', borderRadius: 1, fontFamily: 'monospace', fontSize: 14 }}>
          {recoveryCodes.map((c) => <div key={c}>{c}</div>)}
        </Box>
        <Button
          fullWidth
          variant="outlined"
          sx={{ mb: 1 }}
          onClick={() => navigator.clipboard?.writeText(recoveryCodes.join('\n'))}
        >
          Copy codes
        </Button>
        <Divider sx={{ my: 2 }} />
        <Button fullWidth variant="contained" size="large" onClick={finalize}>
          I've saved my codes — continue
        </Button>
      </Box>
    ));
  }

  // Default: credentials
  return shell('Sign in to your account', (
    <>
      <form onSubmit={handleCredentials}>
        <TextField fullWidth label="Email" name="email" type="email" value={formData.email}
          onChange={handleChange} margin="normal" required autoComplete="email" autoFocus />
        <TextField fullWidth label="Password" name="password" type="password" value={formData.password}
          onChange={handleChange} margin="normal" required autoComplete="current-password" />
        <Button type="submit" fullWidth variant="contained" size="large" disabled={loading} sx={{ mt: 3, mb: 2 }}>
          {loading ? <CircularProgress size={24} /> : 'Sign In'}
        </Button>
      </form>
      <Typography variant="body2" textAlign="center" sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/forgot-password">Forgot password?</Link>
      </Typography>
      <Typography variant="body2" textAlign="center">
        Don't have an account?{' '}
        <Link component={RouterLink} to="/register">Register</Link>
      </Typography>
    </>
  ));
};

export default Login;
